import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import {
  getFormattedContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { CatalogClient, Performer, Venue } from 'src/WebApiController';

const SEARCH_DEBOUNCE_MS = 300;

type FormatFn = (id: FormatContentId, params: string[]) => string;

const venueLabel = (v: Venue, fmt: FormatFn): string => {
  const parts = [v.name];
  const location = [v.city, v.state].filter(Boolean).join(', ');
  if (location) parts.push(location);
  if (v.ctryCode) parts.push(`(${v.ctryCode})`);
  if (v.cap)
    parts.push(fmt(FormatContentId.VenueCapacity, [v.cap.toLocaleString()]));
  return parts.join(' — ');
};

const performerLabel = (p: Performer): string => {
  const parts = [p.name];
  if (p.grName) parts.push(p.grName);
  if (p.categ?.length) parts.push(`[${p.categ.join(', ')}]`);
  return parts.join(' — ');
};

/**
 * Encapsulates the EventGroupInfoStep's data fetching (venue/category/venue-config
 * autocomplete) and the derived dropdown option maps. The component owns the form
 * state and rendering; this hook owns the queries and option computation.
 */
export const useEventGroupInfoStepOptions = () => {
  const { activeAccountWebClientConfig } = useAppContext();

  // Resolver for building dropdown option labels (plain strings, not JSX).
  const contentContext = useContentContext();
  const fmt = useCallback<FormatFn>(
    (id, params) => getFormattedContent(id, params, contentContext) as string,
    [contentContext]
  );

  // Venue search
  const [venueSearchText, setVenueSearchText] = useState('');
  const [debouncedVenueSearch, setDebouncedVenueSearch] = useState('');
  // Track the selected venue object so we can search for its configs.
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);

  // Category search
  const [categorySearchText, setCategorySearchText] = useState('');
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState('');
  // Track the selected category so its label shows without an active search.
  const [selectedCategory, setSelectedCategory] = useState<Performer | null>(
    null
  );

  const debouncedSetVenueSearch = useMemo(
    () =>
      debounce(
        (text: string) => setDebouncedVenueSearch(text),
        SEARCH_DEBOUNCE_MS
      ),
    []
  );

  const debouncedSetCategorySearch = useMemo(
    () =>
      debounce(
        (text: string) => setDebouncedCategorySearch(text),
        SEARCH_DEBOUNCE_MS
      ),
    []
  );

  const onVenueSearchChange = useCallback(
    (text: string) => {
      setVenueSearchText(text);
      debouncedSetVenueSearch(text);
    },
    [debouncedSetVenueSearch]
  );

  const onCategorySearchChange = useCallback(
    (text: string) => {
      setCategorySearchText(text);
      debouncedSetCategorySearch(text);
    },
    [debouncedSetCategorySearch]
  );

  // Venue search query
  const { data: venueResults, isFetching: isVenueLoading } = useQuery({
    queryKey: ['eventGroupVenueSearch', debouncedVenueSearch],
    queryFn: () =>
      new CatalogClient(activeAccountWebClientConfig).searchVenues(
        debouncedVenueSearch
      ),
    enabled: !!debouncedVenueSearch,
    refetchOnWindowFocus: false,
  });

  // Category search query
  const { data: categoryResults, isFetching: isCategoryLoading } = useQuery({
    queryKey: ['eventGroupCategorySearch', debouncedCategorySearch],
    queryFn: () =>
      new CatalogClient(
        activeAccountWebClientConfig
      ).getSearchEventsAutoCompleteResult(
        debouncedCategorySearch,
        20,
        undefined
      ),
    enabled: !!debouncedCategorySearch,
    refetchOnWindowFocus: false,
  });

  // Venue config discovery — autocomplete using selected venue's name to find
  // events at that venue, then extract distinct venCfgIds.
  const { data: venueConfigResults, isFetching: isVenueConfigLoading } =
    useQuery({
      queryKey: ['eventGroupVenueConfigs', selectedVenue?.viagId],
      queryFn: () =>
        new CatalogClient(
          activeAccountWebClientConfig
        ).getSearchEventsAutoCompleteResult(selectedVenue!.name, 50, undefined),
      enabled: selectedVenue != null,
      refetchOnWindowFocus: false,
    });

  /** Selects a venue by viagId (updating internal state) and returns it. */
  const selectVenue = useCallback(
    (viagId: number | null): Venue | null => {
      const venue =
        viagId != null
          ? (venueResults?.find((v) => v.viagId === viagId) ?? null)
          : null;
      setSelectedVenue(venue);
      return venue;
    },
    [venueResults]
  );

  /** Selects a category (performer) by viagId and returns it. */
  const selectCategory = useCallback(
    (viagId: number | null): Performer | null => {
      const performer =
        viagId != null && categoryResults?.performers
          ? (Object.values(categoryResults.performers).find(
              (p) => p?.viagId === viagId
            ) ?? null)
          : null;
      setSelectedCategory(performer);
      return performer;
    },
    [categoryResults]
  );

  const venueOptions = useMemo(() => {
    const opts: Record<string, string> = {};
    // Always include the selected venue so its label shows even without an
    // active search (e.g. after navigating back to this step).
    if (selectedVenue?.viagId != null)
      opts[String(selectedVenue.viagId)] = venueLabel(selectedVenue, fmt);
    venueResults?.forEach((v) => {
      if (v?.viagId != null) opts[String(v.viagId)] = venueLabel(v, fmt);
    });
    return opts;
  }, [venueResults, selectedVenue, fmt]);

  const categoryOptions = useMemo(() => {
    const opts: Record<string, string> = {};
    // Always include the selected category so its label persists across steps.
    if (selectedCategory?.viagId != null)
      opts[String(selectedCategory.viagId)] = performerLabel(selectedCategory);
    if (!categoryResults?.performers) return opts;
    Object.values(categoryResults.performers).forEach((p) => {
      if (p?.viagId != null) opts[String(p.viagId)] = performerLabel(p);
    });
    return opts;
  }, [categoryResults, selectedCategory]);

  const venueConfigOptions = useMemo(() => {
    if (!venueConfigResults?.events) return {};
    // Group event names by venCfgId.
    const groups = new Map<number, string[]>();
    Object.values(venueConfigResults.events).forEach(({ event }) => {
      if (event?.venCfgId == null) return;
      const existing = groups.get(event.venCfgId) ?? [];
      if (event.name && !existing.includes(event.name))
        existing.push(event.name);
      groups.set(event.venCfgId, existing);
    });
    const opts: Record<string, string> = {};
    groups.forEach((names, cfgId) => {
      const detail =
        names.length === 1
          ? names[0]
          : names.length > 1
            ? fmt(FormatContentId.PlusCountMore, [
                names[0],
                String(names.length - 1),
              ])
            : null;
      opts[String(cfgId)] = detail
        ? detail
        : fmt(FormatContentId.ConfigOption, [String(cfgId)]);
    });
    return opts;
  }, [venueConfigResults, fmt]);

  const hasVenueConfigOptions = Object.keys(venueConfigOptions).length > 0;

  return {
    // Venue
    venueSearchText,
    onVenueSearchChange,
    isVenueLoading,
    venueOptions,
    selectVenue,
    // Category
    categorySearchText,
    onCategorySearchChange,
    isCategoryLoading,
    categoryOptions,
    selectCategory,
    // Venue configuration
    isVenueConfigLoading,
    venueConfigOptions,
    hasVenueConfigOptions,
  };
};

export type EventGroupInfoStepOptions = ReturnType<
  typeof useEventGroupInfoStepOptions
>;
